import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft, Send, Download, Calendar, Bot, User } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getConversation,
  getMessages,
  sendMessage,
  updateLastSeen,
  formatMessageTime,
  isUserMessage,
  chatwootWebSocket,
  ChatwootConversation,
  ChatwootMessage,
  updateContact,
  getCurrentContact,
  WebSocketEvent,
} from "../../services/chatwootService";
import "./ChatDetail.scss";

interface ExtendedMessage extends ChatwootMessage {
  status?: "sending" | "sent" | "delivered" | "failed";
}

const ChatDetail: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState<ChatwootConversation | null>(null);
  const [messages, setMessages] = useState<ExtendedMessage[]>([]);
  const [newMessageContent, setNewMessageContent] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [agentTyping, setAgentTyping] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const convId = conversationId ? parseInt(conversationId, 10) : null;

  // Load conversation and messages
  useEffect(() => {
    if (!convId) {
      setLoading(false);
      return;
    }

    const loadConversation = async () => {
      try {
        const convResult = await getConversation(convId);
        if (convResult.success && convResult.data) {
          setConversation(convResult.data);

          // Mark as read
          await updateLastSeen(convId);
        }

        const messagesResult = await getMessages(convId);
        if (messagesResult.success && messagesResult.data) {
          setMessages(
            messagesResult.data.map((msg) => ({
              ...msg,
              status: "delivered" as const,
            }))
          );
        }
      } catch (error) {
        console.error("Failed to load conversation:", error);
      } finally {
        setLoading(false);
      }
    };

    void loadConversation();
  }, [convId]);

  // Auto-scroll to latest message
  useEffect(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, [messages]);

  // WebSocket listeners
  useEffect(() => {
    if (!convId) return;

    const handleNewMessage = (event: WebSocketEvent) => {
      if (event.data.conversation_id !== convId) return;

      const newMsg = event.data as unknown as ChatwootMessage;
      setMessages((prev) => {
        // If the incoming message has an echo_id matching a temp message, replace it
        if (newMsg.echo_id) {
          const idx = prev.findIndex((m) => m.id === newMsg.echo_id);
          if (idx !== -1) {
            const next = [...prev];
            next[idx] = { ...newMsg, status: "delivered" as const };
            return next;
          }
        }
        // Avoid duplicates by real id
        if (prev.some((m) => m.id === newMsg.id)) return prev;
        return [...prev, { ...newMsg, status: "delivered" as const }];
      });

      // Mark as read
      void updateLastSeen(convId);
    };

    const handleTyping = (event: WebSocketEvent) => {
      if (event.data.conversation_id !== convId) return;

      if (event.event === "conversation.typing_on") {
        setAgentTyping(true);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
          setAgentTyping(false);
        }, 3000);
      } else if (event.event === "conversation.typing_off") {
        setAgentTyping(false);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      }
    };

    const handleStatusChange = (event: WebSocketEvent) => {
      if (event.data.id !== convId) return;

      setConversation((prev) =>
        prev
          ? {
              ...prev,
              status: (event.data.status as ChatwootConversation['status']) || prev.status,
            }
          : null
      );
    };

    chatwootWebSocket.on("message.created", handleNewMessage);
    chatwootWebSocket.on("conversation.typing_on", handleTyping);
    chatwootWebSocket.on("conversation.typing_off", handleTyping);
    chatwootWebSocket.on("conversation.status_changed", handleStatusChange);

    return () => {
      chatwootWebSocket.off("message.created", handleNewMessage);
      chatwootWebSocket.off("conversation.typing_on", handleTyping);
      chatwootWebSocket.off("conversation.typing_off", handleTyping);
      chatwootWebSocket.off("conversation.status_changed", handleStatusChange);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [convId]);

  // Handle scroll to show/hide button
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const isScrolledToBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    setShowScrollButton(!isScrolledToBottom);
  };

  const handleSendMessage = async () => {
    if (!newMessageContent.trim() || !convId || sending) return;

    const content = newMessageContent;
    const echoId = `msg_${Date.now()}`;
    const tempMessage: ExtendedMessage = {
      id: echoId,
      content,
      message_type: 0,
      content_type: "text",
      content_attributes: {},
      created_at: Date.now(),
      conversation_id: convId,
      attachments: [],
      sender: {
        id: getCurrentContact()?.id || 0,
        name: getCurrentContact()?.name || "You",
        type: "contact",
      },
      status: "sending",
    };

    setMessages((prev) => [...prev, tempMessage]);
    setNewMessageContent("");
    setSending(true);

    console.log('[ChatDetail] Sending message:', {
      conversationId: convId,
      content: content.substring(0, 50),
      contactId: getCurrentContact()?.id,
      sourceId: getCurrentContact()?.source_id,
    });

    try {
      const result = await sendMessage(convId, content, echoId);
      
      if (result.success) {
        console.log('[ChatDetail] Message sent successfully');
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === echoId ? { ...msg, status: "delivered" } : msg
          )
        );
      } else {
        console.error('[ChatDetail] Failed to send message:', result.error);
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === echoId ? { ...msg, status: "failed" } : msg
          )
        );
      }
    } catch (error) {
      console.error("[ChatDetail] Failed to send message:", error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === echoId ? { ...msg, status: "failed" } : msg
        )
      );
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="chat-detail-container">
        <div className="chat-loading">
          <div className="spinner"></div>
          <p>Loading conversation...</p>
        </div>
      </div>
    );
  }

  if (!convId || !conversation) {
    return (
      <div className="chat-detail-container">
        <div className="chat-error">
          <p>Conversation not found</p>
          <button onClick={() => navigate("/support")}>Back to Support</button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-detail-container">
      {/* Header */}
      <div className="chat-header">
        <button className="back-btn" onClick={() => navigate("/support")}>
          <ArrowLeft size={20} />
        </button>
        <div className="chat-header-info">
          <h2>Ticket #{conversation.id || "N/A"}</h2>
          <span className={`status ${conversation.status}`}>{conversation.status}</span>
        </div>
      </div>

      {/* Messages */}
      <div className="messages-container" ref={messagesContainerRef} onScroll={handleScroll}>
        {messages.length === 0 ? (
          <div className="empty-messages">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <>
            {messages.map((message, index) => {
              const getDateString = (timestamp: any) => {
                try {
                  let ts: number;
                  
                  if (typeof timestamp === "number") {
                    // If number is small (less than 10 billion), assume seconds; otherwise milliseconds
                    ts = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
                  } else if (typeof timestamp === "string") {
                    ts = new Date(timestamp).getTime();
                  } else {
                    return null;
                  }
                  
                  const date = new Date(ts);
                  if (isNaN(date.getTime())) return null;
                  
                  // Return date in consistent format: YYYY-MM-DD
                  return date.toISOString().split('T')[0];
                } catch (e) {
                  console.error('Date parse error:', e, timestamp);
                  return null;
                }
              };

              const currentDate = getDateString(message.created_at);
              const previousDate = index > 0 ? getDateString(messages[index - 1].created_at) : null;
              const showDate = currentDate && (index === 0 || currentDate !== previousDate);

              const formatDateDisplay = (dateStr: string | null) => {
                if (!dateStr) return '';
                try {
                  const date = new Date(dateStr + 'T00:00:00Z');
                  const today = new Date();
                  const yesterday = new Date(today);
                  yesterday.setDate(yesterday.getDate() - 1);
                  
                  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                  const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
                  
                  if (dateOnly.getTime() === todayOnly.getTime()) {
                    return 'Today';
                  } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
                    return 'Yesterday';
                  }
                  
                  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                } catch (e) {
                  return dateStr;
                }
              };

              return (
                <React.Fragment key={message.id}>
                  {showDate && (
                    <div className="date-divider">
                      <Calendar size={14} />
                      <span>{formatDateDisplay(currentDate)}</span>
                    </div>
                  )}

                  <div
                    className={`message ${isUserMessage(message) ? "user" : "agent"}`}
                  >
                    {!isUserMessage(message) && (
                      <div className="message-avatar agent-avatar">
                        <Bot size={18} />
                      </div>
                    )}
                    <div className="message-bubble">
                      <div className="message-content">
                        <div className="message-text">{message.content}</div>
                        {message.attachments && message.attachments.length > 0 && (
                          <div className="message-attachments">
                            {message.attachments.map((att) => (
                              <a
                                key={att.id}
                                href={att.data_url}
                                download
                                className="attachment"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Download size={14} />
                                {att.extension || "File"}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="message-footer">
                        <span className="timestamp">
                          {formatMessageTime(message.created_at)}
                        </span>
                        {isUserMessage(message) && message.status && (
                          <span className={`status ${message.status}`}>
                            {message.status === "sending"
                              ? "⏳"
                              : message.status === "sent"
                                ? "✓"
                                : message.status === "delivered"
                                  ? "✓✓"
                                  : "✗"}
                          </span>
                        )}
                      </div>
                    </div>
                    {isUserMessage(message) && (
                      <div className="message-avatar user-avatar">
                        <User size={18} />
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}

            {agentTyping && (
              <div className="message agent">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                  <p>Agent is typing...</p>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button
          className="scroll-to-bottom-btn"
          onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          ↓ New messages
        </button>
      )}

      {/* Input */}
      <div className="chat-input-area">
        <textarea
          placeholder="Type your message..."
          value={newMessageContent}
          onChange={(e) => setNewMessageContent(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void handleSendMessage();
            }
          }}
          disabled={sending}
        />
        <button
          className="send-btn"
          onClick={() => void handleSendMessage()}
          disabled={!newMessageContent.trim() || sending}
        >
          {sending ? "..." : <Send size={18} />}
        </button>
      </div>
    </div>
  );
};

export default ChatDetail;
